import React, { useMemo, useEffect } from 'react';
import { Inputs }           from '@bagonboard/ui-kit';
import { USER_ROLES }       from '@bagonboard/wiseman';
import InputAdornment       from '@material-ui/core/InputAdornment';
import EmailIcon            from '@material-ui/icons/Email';
import PersonIcon           from '@material-ui/icons/Person';
import LockIcon             from '@material-ui/icons/Lock';
import { FastField, getIn } from 'formik';
import PropTypes            from 'prop-types';

import {
  FormikInput, FormikSelect, PhoneInputContainer, PhoneInputIcon,
} from '../../../../../../../components';
import GridContainer     from '../../../../../../../components/Grid/GridContainer';
import GridItem          from '../../../../../../../components/Grid/GridItem';
import { MandatoryText } from '../../../../../components';
import {
  useCompanies, usePois, useUser, useAreas,
} from '#views/Dashboard/hooks';
import {
  filterRolesToShow,
  mustShowPoi,
  mustShowAreas,
  mustShowCompanies,
  filterCompanies,
} from './logic';
import {
  parseAreas, parseCompanies, parsePois, parseTerminals,
} from './parser';

const { Phone: PhoneInput } = Inputs;

const defaultLocalesList = [{ value: 'en', text: 'English' }, { value: 'es', text: 'Spanish' }];

const UserInfo = ({
  values: formikValues, isDisabled, setFieldValue,
}) => {
  const { data: { role: currentRole, language: currentLang } } = useUser();
  const { pois: { data: pois } } = usePois();
  const { data: areas } = useAreas();
  const { companies: { data: companies }, handlers: companiesHandlers } = useCompanies();
  const listOfRoles = useMemo(() => filterRolesToShow(currentRole, isDisabled), []);
  const listOfAirports = useMemo(() => parsePois(pois, currentLang), []);
  const showAirports = useMemo(() => mustShowPoi(formikValues.role), [formikValues.role]);
  const showAreas = useMemo(() => mustShowAreas(formikValues.role), [formikValues.role]);
  const showCompanies = useMemo(() => mustShowCompanies(formikValues.role, currentRole),
    [formikValues.role, currentRole]);
  const companiesFilter = useMemo(() => filterCompanies(formikValues.role), [formikValues.role]);
  const terminals = useMemo(() => parseTerminals(formikValues.poi, pois), [formikValues.poi, pois]);
  const listOfAreas = useMemo(() => parseAreas(areas), []);
  const listOfCompanies = useMemo(() => parseCompanies(companies), [companies]);
  const isUpdate = Boolean(formikValues.id);

  useEffect(() => {
    if (currentRole === USER_ROLES.ADMIN) {
      companiesHandlers.fetch({ sort: 'type' }, { enableSnackbar: true });
    }
  },
  []);

  return (
    <>
      <MandatoryText />
      <GridContainer spacing={4}>
        <GridItem xs={12} sm={12} md={6}>
          <FormikSelect
            required
            data-testid="input.role"
            name="role"
            labelText="Role selector"
            formControlProps={{
              fullWidth: true,
              disabled : isDisabled || isUpdate,
            }}
            options={listOfRoles}
          />
        </GridItem>
        {
          showCompanies && (
          <GridItem xs={12} sm={12} md={6}>
            <FormikSelect
              required
              data-testid="input.company"
              name="company"
              labelText="Company selector"
              formControlProps={{
                fullWidth: true,
                disabled : isDisabled || isUpdate,
              }}
              options={listOfCompanies.filter(companiesFilter)}
            />
          </GridItem>
          )
        }
        {
        showAirports && (
          <>
            <GridItem xs={12} sm={12} md={6}>
              <FormikSelect
                required
                data-testid="input.poi"
                name="poi"
                labelText="Airport selector"
                formControlProps={{
                  fullWidth: true,
                  disabled : isDisabled,
                }}
                options={listOfAirports}
                modifyOnChange={(...propParams) => {
                  setFieldValue('terminal', '', false);
                  return propParams[0].target.value;
                }}
              />
            </GridItem>
            <GridItem xs={12} sm={12} md={6}>
              <FormikSelect
                required
                data-testid="input.terminal"
                name="terminal"
                labelText="Terminal selector"
                formControlProps={{
                  fullWidth: true,
                  disabled : isDisabled,
                }}
                options={terminals}
              />
            </GridItem>
          </>
        )
      }
        {
        showAreas && (
          <GridItem xs={12} sm={12} md={6}>
            <FormikSelect
              multiple
              data-testid="input.areas"
              name="areas"
              labelText="Areas selector"
              formControlProps={{
                fullWidth: true,
                disabled : isDisabled,
              }}
              options={listOfAreas}
            />
          </GridItem>
        )
      }
        <GridItem xs={12} md={6}>
          <FormikInput
            required
            name="name"
            labelText="Name"
            data-testid="input.name"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              disabled    : isDisabled,
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <FormikInput
            name="surname"
            labelText="Surname"
            data-testid="input.surname"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              disabled    : isDisabled,
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <FastField name="phone">
            {({ field, form }) => {
              const phoneError = form.submitCount && getIn(form.errors, field.name);
              return (
                <PhoneInputContainer
                  error={!!phoneError}
                >
                  <PhoneInput
                    id={field.name}
                    {...field}
                    disabled={isDisabled}
                    placeholder="Phone number"
                    error={phoneError || undefined}
                    onChange={(value) => {
                      form.setFieldValue(field.name, value);
                      form.setFieldTouched(field.name, true);
                    }}
                    defaultCountry="ES"
                  />
                  <PhoneInputIcon />
                </PhoneInputContainer>
              );
            }}
          </FastField>
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <FormikInput
            required
            data-testid="input.email"
            name="email"
            labelText="Email"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              disabled    : isDisabled,
              endAdornment: (
                <InputAdornment position="end">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <FormikInput
            required={!isUpdate}
            data-testid="input.password"
            name="password"
            labelText="Password"
            formControlProps={{
              fullWidth: true,
            }}
            inputProps={{
              disabled    : isDisabled,
              endAdornment: (
                <InputAdornment position="end">
                  <LockIcon />
                </InputAdornment>
              ),
              type: 'password',
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <FormikSelect
            data-testid="input.language"
            name="language"
            labelText="Language selector"
            formControlProps={{
              fullWidth: true,
              disabled : isDisabled,
            }}
            options={defaultLocalesList}
          />
        </GridItem>
      </GridContainer>
    </>
  );
};

UserInfo.propTypes = {
  values: PropTypes.shape({
    role: PropTypes.string,
  }),
  isDisabled   : PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

UserInfo.defaultProps = {
  values: {},
};

export default UserInfo;
